import React, { Fragment, useEffect, useState } from 'react';
import { Button, Typography, Checkbox, FormControlLabel } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import {
  GetProjectInvitationPrefillDataDocument,
  AcceptProjectInvitationDocument,
  SignInDocument,
  CompaniesHouseInfo,
} from '@/graphql/generated/graphql';
import { DEFAULT_COMPANY_INFO, passwordRequirements } from '@/utils/constants';
import { displayNotification } from '@/utils/notifications';
import { SignInContainer, WiderStyledCard } from '../Auth/StyledComponents';
import { isUndefined, omit } from 'lodash';
import CompanyDetailsFields from '../OnboardingPage/CompanyDetailsFields';
import UserDetails from './UserDetails';
import CircularLoader from '@/components/Loaders/CircularLoader';

const DEFAULT_USER_INFO = { firstName: '', lastName: '', email: '', password: '' };
const REQUIRED_COMPANY_FIELDS: Array<keyof typeof DEFAULT_COMPANY_INFO> = [
  'name',
  'companyNumber',
  'email',
  'street',
  'zip',
];

const ProjectInvitationAcceptancePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [prefillData, { loading: loadingPrefilData }] = useLazyQuery(GetProjectInvitationPrefillDataDocument, {
    fetchPolicy: 'network-only',
  });

  const [companyInfo, setCompanyInfo] = useState(DEFAULT_COMPANY_INFO);
  const [selectedState, setSelectedState] = useState<string | null>(null);
  const [selectedCity, setSelectedCity] = useState<string | null>(null);
  const [userInfo, setUserInfo] = useState(DEFAULT_USER_INFO);
  const [adminInfo, setAdminInfo] = useState<typeof DEFAULT_USER_INFO>(DEFAULT_USER_INFO);
  const [showCustomAdminFields, setShowCustomAdminFields] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const token = searchParams.get('token');
    if (token) {
      prefillData({ variables: { token } }).then((res) => {
        if (res.data?.getProjectInvitationPrefillData) {
          const { firstName, lastName, email, companyName, companyPhone } = res.data.getProjectInvitationPrefillData;
          setCompanyInfo({ ...companyInfo, name: companyName as string, phone: companyPhone as string });
          setUserInfo({
            ...userInfo,
            firstName: firstName as string,
            lastName: lastName as string,
            email: email as string,
          });
        }
      });
    }
  }, [searchParams]);

  const [signIn, { loading: loadingSigningIn }] = useMutation(SignInDocument, {
    onCompleted: () => {
      navigate('/');
    },
    onError: (error) => displayNotification({ type: 'error', text: error.message }),
  });
  const [acceptProjectInvite, { loading: loadingAcceptance }] = useMutation(AcceptProjectInvitationDocument, {
    variables: {
      token: searchParams?.get('token') || '',
    },
    onCompleted: () => {
      signIn({ variables: { email: userInfo.email, password: userInfo.password, rememberMe: false } });
    },
    onError: (error) => displayNotification({ type: 'error', text: error.message }),
  });

  const validateFields = () =>
    REQUIRED_COMPANY_FIELDS.every((field) => companyInfo[field]?.trim() !== '') &&
    selectedCity !== null &&
    selectedState !== null &&
    Object.values(userInfo).every((value) => (value as string).trim() !== '') &&
    passwordRequirements.every((req) => req.test(userInfo.password)) &&
    (showCustomAdminFields ? Object.values(omit(adminInfo, ['password'])).every((value) => value.trim() !== '') : true); // only validate admin fields if they were enabled by the user

  const handleSubmit = () => {
    // call the mutations here
    acceptProjectInvite({
      variables: {
        token: searchParams?.get('token') || '',
        company: {
          name: companyInfo.name,
          tradingName: companyInfo.tradingName,
          companyNumber: companyInfo.companyNumber,
          vatNumber: companyInfo.vatNumber,
          companyType: companyInfo.companyType,
          primaryEmail: companyInfo.email,
          primaryPhone: companyInfo.phone,
          street: companyInfo.street,
          city: selectedCity as string,
          state: selectedState as string,
          country: 'United Kingdom',
          zip: companyInfo.zip,
        },
        user: userInfo,
        ...(showCustomAdminFields && { admin: adminInfo }),
      },
    });
  };

  const handleAutofill = (companiesHouseInfo: CompaniesHouseInfo) => {
    if (isUndefined(companiesHouseInfo) || !companiesHouseInfo?.success || !companiesHouseInfo?.data) return;

    const { name, companyType, address } = companiesHouseInfo.data;

    setCompanyInfo((prev) => ({ ...prev,
      name,
      street: address?.street ?? prev.street,
      city: address?.city ?? "",
      state: address?.state ?? "",
      zip: address?.zip ?? prev.zip,
      companyType: companyType ?? prev.companyType,
    }));
  }

  if (loadingPrefilData) return <CircularLoader />;

  return (
    <SignInContainer direction="column" justifyContent="space-between" sx={{ mt: 5 }}>
      <WiderStyledCard variant="outlined">
        <Typography variant="h4" textAlign="center" fontWeight={600}>
          Welcome to TheOverwatch.AI
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 2 }}>
          Please fill out the following form to accept your invitation.
        </Typography>

        <CompanyDetailsFields
          companyInfo={companyInfo}
          onFieldChange={(key, value) => setCompanyInfo((prev: typeof companyInfo) => ({ ...prev, [key]: value }))}
          selectedState={selectedState}
          selectedCity={selectedCity}
          onSelectedCityChange={setSelectedCity}
          onSelectedStateChange={setSelectedState}
          handleAutofill={handleAutofill}
        />

        <Typography variant="subtitle2" sx={{ mt: 1 }}>
          Admin User
        </Typography>

        <UserDetails
          key="default-user"
          userInfo={userInfo}
          onFieldChange={(field, value) => setUserInfo((prev: typeof userInfo) => ({ ...prev, [field]: value }))}
        />

        <FormControlLabel
          control={<Checkbox onChange={(_, checked) => setShowCustomAdminFields(checked)} />}
          label="Add different user as admin?"
        />

        {showCustomAdminFields && (
          <Fragment>
            <Typography variant="subtitle2">
              An invite will be sent to the email address provided and they will be asked to set a password.
            </Typography>
            <UserDetails
              key="admin-user"
              userInfo={adminInfo}
              onFieldChange={(field, value) => setAdminInfo((prev: typeof adminInfo) => ({ ...prev, [field]: value }))}
              showPassword={false}
            />
          </Fragment>
        )}

        <Button
          fullWidth
          variant="contained"
          onClick={handleSubmit}
          disabled={!validateFields() || loadingAcceptance || loadingSigningIn}
        >
          Continue
        </Button>

        <Typography variant="caption" textAlign="center">
          By accepting this invitation, your user account's password will be set and the project you were invited on
          will be imported into your workspace.
        </Typography>
      </WiderStyledCard>
    </SignInContainer>
  );
};

export default ProjectInvitationAcceptancePage;
